import { usePageModels } from '@/context/PageModels';
import { DataLayerEventNames, DataLayerEvents } from '@/datalayer/App.datalayer.constants';
import { useLog } from '@/features/shared/Hooks/useLog';
import React from 'react';

const GTM_EVENTS = [DataLayerEventNames.GTM_EVENT];

/**
 * This function will validate if the event is a GTM event.
 */
export const validateGTMEvent = (event: string) =>
  GTM_EVENTS.some((gtmEvent) => {
    const regex = new RegExp(gtmEvent);
    return regex.test(event);
  });

/**
 * This function will wait for the GTM to be loaded.
 */
export const waitForGTMLoaded = (customDataLayerPush = () => {}) => {
  let interval: NodeJS.Timeout | null = null;
  const promise = new Promise<void>((resolve) => {
    interval = setInterval(() => {
      if (window.dataLayer.push !== customDataLayerPush) {
        if (interval) clearInterval(interval);
        resolve();
      }
    }, 100);
  });

  return { interval, promise };
};

/**
 * Dispatch the event to the window.
 */
export const dispatchEvent = (dataLayer: any) => {
  const pushEvent = new CustomEvent(DataLayerEvents.DATALAYER_PUSH_EVENT, {
    detail: dataLayer,
  });

  window.dispatchEvent(pushEvent);
};

/**
 * This hook will intercept the dataLayer events and will queue them until the GTM is loaded.
 */
export const useDataLayerInterceptor = () => {
  const { datalayerEventBlacklist = [] } = usePageModels();
  const datalayerEventBlacklistRef = React.useRef(datalayerEventBlacklist);
  const waitingForGTMRef = React.useRef<any[]>([]);
  const gtmLoadedRef = React.useRef(false);
  const [isReady, setIsReady] = React.useState(false);
  const Log = useLog({ context: 'useDataLayerInterceptor' });

  /**
   * This function will queue all events that are pushed to the dataLayer before the GTM is loaded.
   */
  const queueEventsBeforeGTMLoaded = React.useCallback(() => {
    /**
     * Initialize the dataLayer if it is not initialized.
     */
    window.dataLayer = window.dataLayer || [];

    console.log(
      'queueEventsBeforeGTMLoaded started',
      window?.dataLayer?.map((props) => props),
    );

    /**
     * Save the original push method of the dataLayer and the original dataLayer itself.
     */
    const originalPush = window.dataLayer?.push;
    const originalDataLayer = window.dataLayer;

    /**
     * Reset the dataLayer.
     */
    window.dataLayer = [];

    Log(
      'queueEventsBeforeGTMLoaded started',
      window?.dataLayer?.map((props) => props),
    );

    /**
     * This function will be used as the interceptor.
     */
    const customDataLayerPush = (...args: any[]) => {
      const [dataLayer] = args;
      const isGTMEvent = validateGTMEvent(dataLayer?.event);
      const isPageLoadEvent = Boolean(dataLayer.original_page_path);
      const isGTMLoaded = gtmLoadedRef.current;

      /**
       * Return early if the GTM is loaded, or if the event is gtm.js,
       * or if is a page load event.
       *
       * Push the event to the dataLayer.
       */
      if (isGTMLoaded || isGTMEvent || isPageLoadEvent) {
        const result = originalPush.call(window.dataLayer, ...args);

        /**
         * Dispatch the event to the window if the event is a GTM event or Page Load event.
         */
        if (isGTMEvent || isPageLoadEvent) {
          Log('Dispatching event to the window', dataLayer);
          dispatchEvent(dataLayer);
        }

        return result;
      }

      /**
       * Push the event to the waitingForGTMRef queue.
       */
      Log('Pushing event to the queue', dataLayer);
      waitingForGTMRef.current.push(dataLayer);

      /**
       * Return the length of the dataLayer.
       */
      return window.dataLayer.length;
    };

    /**
     * Intercept the push method of the dataLayer.
     */
    window.dataLayer.push = customDataLayerPush;

    /**
     * Push the original dataLayer to the new dataLayer.
     * This will push all the events that were pushed before the GTM was loaded.
     */
    originalDataLayer.forEach((dataLayer) => window.dataLayer.push(dataLayer));

    /**
     * Return the custom datalayer push method.
     */
    return customDataLayerPush;

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * This function will flush the waitingForGTMRef queue.
   */
  const flushEventsWaitingForGTMLoaded = React.useCallback(() => {
    console.log('flushEventsWaitingForGTMLoaded', waitingForGTMRef.current);

    waitingForGTMRef.current.forEach((dataLayer) => {
      Log('Flushing event from the queue', dataLayer);

      const isInBlackList = datalayerEventBlacklistRef.current.includes(dataLayer?.event);
      if (isInBlackList) {
        Log('Event is in the blacklist', dataLayer);
        return;
      }

      window.dataLayer.push(dataLayer);
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Initialize the dataLayer interceptor on top of the GTM JS.
   * 1. Blacklist events that are not allowed to be pushed to the dataLayer.
   * 2. Dispatch the event to the window.
   */
  const initialize = React.useCallback(async () => {
    Log('Initialize interceptor', window.dataLayer?.push);

    /**
     * Wait .25s before intercepting the push method of the dataLayer.
     */
    await new Promise((resolve) => setTimeout(resolve, 250));

    const originalPush = window.dataLayer?.push;

    /**
     * Intercept the push method of the dataLayer.
     */
    const customDataLayerPush = (...args: any[]) => {
      const [dataLayer] = args;
      const hasDataLayerEvent = dataLayer?.event !== undefined;

      /**
       * Return early if the event is not defined.
       */
      if (!hasDataLayerEvent) return originalPush.call(window.dataLayer, ...args);

      Log('Attempt to push event to datalayer', dataLayer);

      /**
       * Return early if the event is in the blacklist.
       */
      const isInBlackList = datalayerEventBlacklistRef.current.includes(dataLayer?.event);
      if (isInBlackList) {
        Log('Event is in the blacklist', dataLayer);
        return window.dataLayer.length;
      }

      /**
       * Push the event to the dataLayer.
       */
      Log('Push Event to datalayer', dataLayer);
      const result = originalPush.call(window.dataLayer, ...args);

      dispatchEvent(dataLayer);

      /**
       * Return the length of the dataLayer.
       */
      return result;
    };

    window.dataLayer.push = customDataLayerPush;

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Starts the dataLayer interceptor flow.
   */
  React.useEffect(() => {
    /**
     * Return early if the page is in preview mode.
     */
    if (window.isPreview) return () => {};

    /**
     * Queue the events that are pushed to the dataLayer before the GTM is loaded.
     * This will return the customDataLayerPush function that will be used to intercept the dataLayer events.
     */
    const customDataLayerPush = queueEventsBeforeGTMLoaded();

    /**
     * Wait for the GTM to be loaded.
     */
    const { interval, promise } = waitForGTMLoaded(customDataLayerPush);

    /**
     * Resolve the promise when the GTM is loaded.
     */
    promise.then(async () => {
      Log('GTM is loaded');
      gtmLoadedRef.current = true;

      /**
       * Initialize the dataLayer interceptor.
       */
      await initialize();

      /**
       * Flush the queue of events that were pushed to the dataLayer before the GTM JS was loaded.
       */
      flushEventsWaitingForGTMLoaded();
    });

    /**
     * Set the isReady flag to true.
     */
    Promise.resolve().then(() => {
      setTimeout(() => {
        setIsReady(true);
      }, 100);
    });

    /**
     * Clear the interval if the component is unmounted.
     */
    return () => {
      if (interval) clearInterval(interval);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { isReady };
};
